import {
  Row,
  Col,
  Form,
  FormGroup,
  Label,
  Input,
  Card,
  CardBody,
  Button,
  InputGroup,
} from "reactstrap";
import Select from "react-select";
import { SettingsProps } from "../../types/settings";
import { useFormik } from "formik";

import { BranchType } from "types";
import { useGetBranchById, useUpdateBranch } from "hooks/useBranches";
import { useSelector } from "hooks/useSelector";
import { RootState } from "slices";
import SettingsBranchDropdown from "../../Components/Common/Branch/BranchDropdown";
import { BranchValidationSchema } from "utils";
import Flatpickr from "react-flatpickr";
import { toast } from "react-toastify";
import { useEffect } from "react";

interface DayOption {
  value: string;
  label: string;
}
interface PreferenceOption {
  value: string;
  label: string;
}
interface CurrencyOption {
  value: string;
  label: string;
}

const BranchSettings: React.FC<SettingsProps> = ({
  onSave,
  onGlobalCancel,
  isSaving,
}) => {
  const { mutate } = useUpdateBranch(); // Hook to update branch

  const selectedBranch = useSelector(
    (state: RootState) => state.branch?.currentBranch
  );

  const { data: branchData } = useGetBranchById(String(selectedBranch?.id));

  const customStyles = {
    multiValue: (base: any, state: any) => ({
      ...base,
      display: state.data.value ? "flex" : "none",
    }),
    valueContainer: (base: any, state: any) => ({
      ...base,
      // Only show the padding when there are values
      padding: state.hasValue ? "2px 8px" : "2px 8px",
    }),
    // Hide the empty containers completely
    multiValueContainer: (base: any, state: any) => ({
      ...base,
      display: state.data.value ? "flex" : "none",
    }),
  };

  const workingDaysOptions: DayOption[] = [
    { value: "monday", label: "Monday" },
    { value: "tuesday", label: "Tuesday" },
    { value: "wednesday", label: "Wednesday" },
    { value: "thursday", label: "Thursday" },
    { value: "friday", label: "Friday" },
    { value: "saturday", label: "Saturday" },
    { value: "sunday", label: "Sunday" },
  ];
  const branchPreferenceOptions: PreferenceOption[] = [
    { value: "Is Main Branch", label: "Set as Main Branch" },
    { value: "Allows PickUp", label: "Allows PickUp" },
    { value: "Allows Delivery", label: "Allows Delivery" },
  ];
  const currencyOptions: CurrencyOption[] = [
    { value: "GHS", label: "GHS - Ghanaian Cedi" },
    { value: "USD", label: "USD - US Dollar" },
    { value: "EUR", label: "EUR - Euro" },
  ];

  const formik = useFormik<
    Omit<BranchType, "working_days" | "currency"> & {
      working_days: DayOption[];
      branch_preference: PreferenceOption[];
      currency: string;
    }
  >({
    initialValues: {
      id: selectedBranch?.id || 0,
      name: branchData?.name,
      branch_code: branchData?.branch_code || "",
      address: branchData?.address,
      phone_number: branchData?.phone_number || "",
      email: branchData?.email || "",
      currency: branchData?.currency || "",
      manager_name: branchData?.manager_name || "",
      manager_phone: branchData?.manager_phone || "",
      opening_time: branchData?.opening_time || "",
      closing_time: branchData?.closing_time || "",
      working_days: branchData?.working_days
        ? branchData?.working_days?.split(",").map((d: string) => ({
            value: d,
            label: d.charAt(0).toUpperCase() + d.slice(1),
          }))
        : [],

      branch_preference: branchData?.branch_preference
        ? branchData?.branch_preference.split(",").map((b: string) => ({
            value: b,
            label: b.charAt(0).toUpperCase() + b.slice(1),
          }))
        : [],
    },

    enableReinitialize: true,
    validationSchema: BranchValidationSchema,

    onSubmit: async (values) => {
      if (!values.id) {
        toast.error("Branch ID is missing.");
        return;
      }

      const payload = {
        id: values.id,
        name: values.name,
        branch_code: values.branch_code,
        address: values.address,
        phone_number: values.phone_number,
        email: values.email,
        currency: values?.currency || "",
        manager_name: values.manager_name,
        manager_phone: values.manager_phone,
        opening_time: values.opening_time,
        closing_time: values.closing_time,
        working_days: values.working_days
          ?.map((day: DayOption) => day.value)
          .join(","),
        branch_preference: values.branch_preference
          ?.map((day: PreferenceOption) => day.value)
          .join(","),
      };

      //mutate for updating branch data
      mutate({ id: Number(values.id), data: payload });
    },
  });

  const handleCancel = () => {
    formik.resetForm();
  };

  useEffect(() => {
    if (selectedBranch && branchData) {
      formik.resetForm();
      formik.setValues({
        // ...formik.values,
        id: selectedBranch.id,
        name: branchData.name,
        branch_code: branchData?.branch_code || "",
        address: branchData.address,
        phone_number: branchData.phone_number,
        email: branchData.email,
        currency: branchData?.currency,
        manager_name: branchData.manager_name,
        manager_phone: branchData.manager_phone,
        opening_time: branchData.opening_time,
        closing_time: branchData.closing_time,
        working_days:
          branchData.working_days?.split(",").map((d: string) => ({
            value: d,
            label: d.charAt(0).toUpperCase() + d.slice(1),
          })) || [],
        branch_preference:
          branchData.branch_preference?.split(",").map((d: string) => ({
            value: d,
            label: d.charAt(0).toUpperCase() + d.slice(1),
          })) || [],
      });
    }
  }, [selectedBranch, branchData]);

  console.log("Formik Errors:", formik.errors);
  console.log("Formik Touched:", formik.touched);

  return (
    <div className="bg-transparent">
      <div className="d-flex justify-content-between align-items-center">
        <h5 className="mb-4">
          Branch Settings - {selectedBranch?.name || "Loading..."}
        </h5>

        <SettingsBranchDropdown />
      </div>

      <Form onSubmit={formik.handleSubmit}>
        <Card className="mb-4">
          <CardBody>
            <h6 className="mb-3">Branch Information</h6>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="branchName">Branch Name</Label>
                  <Input
                    type="text"
                    id="name"
                    name="name"
                    placeholder="Enter branch name"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.name}
                    invalid={!!(formik.touched.name && formik.errors.name)}
                  />
                  {formik.touched.name && formik.errors.name && (
                    <div className="text-danger">{formik.errors.name}</div>
                  )}
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label for="branchCode">Branch Code</Label>
                  <Input
                    type="text"
                    id="branchCode"
                    name="branch_code"
                    placeholder="Enter branch code"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.branch_code}
                    invalid={
                      !!(
                        formik.touched.branch_code && formik.errors.branch_code
                      )
                    }
                  />
                  {formik.touched.branch_code && formik.errors.branch_code && (
                    <div className="text-danger">
                      {formik.errors.branch_code}
                    </div>
                  )}
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col md={12}>
                <FormGroup>
                  <Label for="branchAddress">Branch Address</Label>
                  <Input
                    type="textarea"
                    id=" branchAddress"
                    name="address"
                    rows={3}
                    placeholder="Enter branch address"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.address}
                    invalid={
                      !!(formik.touched.address && formik.errors.address)
                    }
                  />
                  {formik.touched.address && formik.errors.address && (
                    <div className="text-danger">{formik.errors.address}</div>
                  )}
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="branchPhone">Phone Number</Label>
                  <Input
                    type="tel"
                    id="phoneNumber"
                    name="phone_number"
                    placeholder="Enter branch phone number"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.phone_number}
                    invalid={
                      !!(
                        formik.touched.phone_number &&
                        formik.errors.phone_number
                      )
                    }
                  />
                  {formik.touched.phone_number &&
                    formik.errors.phone_number && (
                      <div className="text-danger">
                        {formik.errors.phone_number}
                      </div>
                    )}
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label for="branchEmail">Email</Label>
                  <Input
                    type="email"
                    id="branchEmail"
                    name="email"
                    placeholder="Enter branch email"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.email}
                    invalid={!!(formik.touched.email && formik.errors.email)}
                  />
                  {formik.touched.email && formik.errors.email && (
                    <div className="text-danger">{formik.errors.email}</div>
                  )}
                </FormGroup>
              </Col>
            </Row>

            <hr className="my-4" />

            <h6 className="mb-3">Branch Manager</h6>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="managerName">Manager Name</Label>
                  <Input
                    type="text"
                    id="managerName"
                    name="manager_name"
                    placeholder="Enter manager name"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.manager_name}
                    invalid={
                      !!(
                        formik.touched.manager_name &&
                        formik.errors.manager_name
                      )
                    }
                  />
                  {formik.touched.manager_name &&
                    formik.errors.manager_name && (
                      <div className="text-danger">
                        {formik.errors.manager_name}
                      </div>
                    )}
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label for="managerPhone">Manager Phone</Label>
                  <Input
                    type="tel"
                    id="managerPhone"
                    name="manager_phone"
                    placeholder="Enter manager phone"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.manager_phone}
                    invalid={
                      !!(
                        formik.touched.manager_phone &&
                        formik.errors.manager_phone
                      )
                    }
                  />
                  {formik.touched.manager_phone &&
                    formik.errors.manager_phone && (
                      <div className="text-danger">
                        {formik.errors.manager_phone}
                      </div>
                    )}
                </FormGroup>
              </Col>
            </Row>

            <hr className="my-4" />

            <h6 className="mb-3">Operating Hours</h6>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="opening_time">Opening Time</Label>
                  <InputGroup>
                    <Flatpickr
                      id="opening_time"
                      name="opening_time"
                      className="form-control d-block"
                      placeholder="Select opening time"
                      value={
                        formik.values.opening_time
                          ? new Date(`1970-01-01T${formik.values.opening_time}`)
                          : undefined
                      }
                      onChange={(date: Date[]) => {
                        formik.setFieldValue(
                          "opening_time",
                          date.length
                            ? date[0].toISOString().split("T")[1]
                            : null
                        );
                      }}
                      options={{
                        enableTime: true,
                        noCalendar: true,
                        dateFormat: "H:i",
                      }}
                    />
                    <div className="input-group-append">
                      <span className="input-group-text">
                        <i className="mdi mdi-clock-outline" />
                      </span>
                    </div>
                  </InputGroup>
                  {formik.touched.opening_time &&
                    formik.errors.opening_time && (
                      <div className="text-danger">
                        {formik.errors.opening_time}
                      </div>
                    )}
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label for="closing_time">Closing Time</Label>
                  <InputGroup>
                    <Flatpickr
                      id="closing_time"
                      name="closing_time"
                      className="form-control d-block"
                      placeholder="Select closing time"
                      value={
                        formik.values.closing_time
                          ? new Date(`1970-01-01T${formik.values.closing_time}`)
                          : undefined
                      }
                      onChange={(date: Date[]) => {
                        formik.setFieldValue(
                          "closing_time",
                          date.length
                            ? date[0].toISOString().split("T")[1]
                            : null
                        );
                      }}
                      options={{
                        enableTime: true,
                        noCalendar: true,
                        dateFormat: "H:i",
                      }}
                    />
                    <div className="input-group-append">
                      <span className="input-group-text">
                        <i className="mdi mdi-clock-outline" />
                      </span>
                    </div>
                  </InputGroup>
                  {formik.touched.closing_time &&
                    formik.errors.closing_time && (
                      <div className="text-danger">
                        {formik.errors.closing_time}
                      </div>
                    )}
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col md={12}>
                <FormGroup>
                  <Label for="workingDays">Working Days</Label>
                  <Select<DayOption, true>
                    isMulti
                    id="workingDays"
                    name="working_days"
                    value={formik.values.working_days}
                    placeholder="Select working days"
                    onChange={(options) =>
                      formik.setFieldValue("working_days", options)
                    }
                    options={workingDaysOptions}
                    styles={customStyles}
                    className={
                      formik.touched.working_days && formik.errors.working_days
                        ? "is-invalid"
                        : ""
                    }
                  />
                  {formik.touched.working_days &&
                    formik.errors.working_days && (
                      <div className="text-danger">
                        {/* {formik.errors.working_days} */}
                      </div>
                    )}
                </FormGroup>
              </Col>
            </Row>

            <hr className="my-4" />

            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="branchPreference">Branch Preference</Label>
                  <Select<DayOption, true>
                    isMulti
                    id="branchPreference"
                    name="branch_preference"
                    value={formik.values.branch_preference}
                    placeholder="Select branch preference"
                    onChange={(options) =>
                      formik.setFieldValue("branch_preference", options)
                    }
                    options={branchPreferenceOptions}
                    styles={customStyles}
                    className={
                      formik.touched.branch_preference &&
                      formik.errors.branch_preference
                        ? "is-invalid"
                        : ""
                    }
                  />
                  {formik.touched.branch_preference &&
                    formik.errors.branch_preference && (
                      <div className="text-danger">
                        {/* {formik.errors.branch_preference} */}
                      </div>
                    )}
                </FormGroup>
              </Col>

              <Col md={6}>
                <FormGroup>
                  <Label for="currency">Currency</Label>
                  <Select
                    id="currency"
                    name="currency"
                    // value={formik.values.currency}
                    value={
                      formik.values.currency
                        ? currencyOptions?.find(
                            (b) => b.value === formik.values?.currency
                          )
                        : null
                    }
                    placeholder="Select branch currency"
                    onChange={(option: CurrencyOption | null) =>
                      formik.setFieldValue("currency", option?.value)
                    }
                    options={currencyOptions}
                    styles={customStyles}
                    className={
                      formik.touched.currency && formik.errors.currency
                        ? "is-invalid"
                        : ""
                    }
                  />
                  {formik.touched.currency && formik.errors.currency && (
                    <div className="text-danger">
                      {/* {formik.errors.currency} */}
                    </div>
                  )}
                </FormGroup>
              </Col>
            </Row>
          </CardBody>
        </Card>

        <div className="d-flex justify-content-end mt-3">
          <Button
            color="secondary"
            className="w-10 me-1"
            style={{ width: "20%" }}
            onClick={handleCancel} // Handle cancel action
          >
            Reset
          </Button>

          <Button
            type="submit"
            color="primary"
            className="w-10 ms-1"
            style={{ width: "20%" }}
          >
            Save Changes
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default BranchSettings;
